<template>
	<div id="app" class="text-dark">
		<div id="header" class="shadow-sm sticky-top" :class="(!clientKey) ? 'invisible': ''">
			<transition name="fade" key="transition.header" appear>
				<b-navbar ref="mainHeader" tag="header" class="w-100" sticky type="light" variant="light">
					<b-navbar-brand to="/"><img :src="logo" style="height: 48px;" :alt="clientConfig.name" v-if="clientConfig && clientConfig.name"></b-navbar-brand>
					<b-navbar-nav class="ml-auto">
						<b-nav-text class="mb-0 pb-0"><transition name="fade" appear><h3 v-if="profile"><b>{{ profile.institution.name || profile.institution }}</b> | {{ profile.firstName }} {{ profile.lastName }}</h3></transition></b-nav-text>
					</b-navbar-nav>
				</b-navbar>
			</transition>
		</div>
		<div id="body">
			<template v-if="isReady && ((profile && profile.uuid) || (/^demo/i.test($route.name)))">
				<transition name="fade" key="transition.main-content" appear>
					<div id="main-content" key="main-content" class="mw-100">
						<router-view ref="view" :key="$route.fullPath" />
					</div>
				</transition>
			</template>
			<template v-else-if="isReady && !clientKey">
				<transition name="fade" key="transition.main-content" appear>
					<div id="main-content" key="main-content" class="mw-100">
						<b-container class="w-75 text-dark h-100">
							<b-row class="heading h-100 m-0 p-0" align-v="center">
								<b-col class="text-center" style="margin-bottom: 64px;">
									<img :src="require('@/assets/propeller-color-icon.svg')" class="m-5" style="height: 256px; filter: drop-shadow(1px 1px 1px rgb(32,32,32));" alt="Propeller"><br/>
									Well this is unexpected. Please contact your school's support desk or <a href="mailto:help@propeller.cloud">email help@propeller.cloud</a>.
								</b-col>
							</b-row>
						</b-container>
					</div>
				</transition>
			</template>
			<template v-else-if="isReady && isMaintenanceMode">
				<transition name="fade" key="transition.main-content" appear>
					<div id="main-content" key="main-content" class="mw-100" :class="((!maintenanceMessages.length) ? 'my-auto' : undefined)">
						<b-container class="w-75 text-dark h-100">
							<b-row class="heading m-0 mx-auto p-0" style="width: 320px; height: 320px;" align-v="center">
								<b-col class="text-center">
									<img :src="require('@/assets/propeller-color-icon.svg')" class="my-4" style="height: 256px; filter: drop-shadow(1px 1px 1px rgb(32,32,32));" alt="Propeller"><br/>
								</b-col>
							</b-row>
							<b-row class="m-0 p-0">
								<b-col class="text-center">
									<p class="h2">Sorry, we'll be back in a bit!</p>
									<p>The system is currently undergoing routine maintenance. Please check back soon.</p>
									<p>Thank you for your patience!</p>
									<b-container class="w-75 mt-5 small border border-warning shadow-sm" v-if="maintenanceMessages.length">
										<b-row class="bg-warning font-weight-bold text-left p-2">
											<b-col>
												Updates
											</b-col>
										</b-row>
										<template v-for="m in maintenanceMessages">
											<b-row class="border-bottom p-2">
												<b-col style="min-width: 30%; max-width: 30%;">{{ $dayjs(m.date).format('YYYY/MM/DD HH:mm:ss') }}</b-col>
												<b-col class="w-auto text-left">{{ m.message }}</b-col>
											</b-row>
										</template>
									</b-container>
								</b-col>
							</b-row>
						</b-container>
					</div>
				</transition>
			</template>
			<elumin-loader v-else />
		</div>
		<div id="footer" class="mx-auto bg-dark text-secondary">
			<b-row no-gutters>
				<b-col class="text-center">
					<span id="powered-by-notice" class="mx-1" :class="(!shouldShowPoweredBy) ? 'd-none': ''">Powered by <a href="https://www.elumin.co"><img :style="'opacity: ' + 0.3 + ';'" :src="require(`@/assets/elumin-logo-white.svg`)" style="max-height: 12px;" alt="eLumin"></a></span>
					<span id="copyright-notice" class="align-middle mx-1" :class="(!shouldShowCopyrightFooter) ? 'd-none': ''">&copy; {{ (new Date()).getFullYear() }}, eLumin LLC, All rights reserved.</span>
					<span>v{{ build.version }} ({{ build.date }})</span>
				</b-col>
			</b-row>
		</div>
		<ErrorModal :ref="errorModalData.id" :modalData="errorModalData" />
		<template v-if="clientConfig.clientCode">
			<b-modal id="UnknownUserErrorModal" :visible="!profile.uuid" header-bg-variant="danger" header-text-variant="light" no-close-on-backdrop no-close-on-esc hide-header-close hide-footer v-if="profile">
				<div slot="modal-title">Error</div>
				<b-container class="text-center">
					<b-row>
						<b-col class="text-justify">Sorry {{ profile.firstName }}! We could not find an enrollment record for you. If this is unexpected, please contact your school's support desk or <a :href="'mailto:help@propeller.cloud'">email help@propeller.cloud</a>.</b-col>
					</b-row>
				</b-container>
			</b-modal>
		</template>
	</div>
	
</template>

<script>
	import Vue from 'vue';
	import VueMeta from 'vue-meta';
	import VueTimers from 'vue-timers';
	
	import { mapMutations, mapState } from 'vuex';
	import { library } from '@fortawesome/fontawesome-svg-core';
	import { faCaretDown, faCaretRight, faQuestion, faFrown } from '@fortawesome/free-solid-svg-icons';
	
	Vue.use(
		VueMeta,
		{
			refreshOnceOnNavigation: true
		}
	);
	Vue.use(VueTimers);
	
	library.add(faCaretDown, faCaretRight, faQuestion);
	
	let listeners = [];
	
	const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
	
	export default
	{
		name: 'app',
		metaInfo()
		{
			const title = ((this.whiteLabelTitle) ? '' : 'Propeller');
			
			return {
				title,
				titleTemplate: '%s Platform Portal'
			};
		},
		computed:
		{
			...mapState([ 'clientKey', 'clientConfig', 'authSession', 'userSession', 'profile', 'loadedAt' ]),
			
			appName: function()
			{
				return (process.env.VUE_APP_NAME || ((window.location.hostname != 's3.amazonaws.com') ? window.location.hostname.split('.')[0] : (new URL(window.location.href)).pathname.split('/')[2]));
			},
			apiURL: function()
			{
				return (process.env.VUE_APP_CLIENT_API || this.clientConfig.apiURLs[this.appName]) + '/ck:' + this.clientKey;
			},
			apiAuthURL: function()
			{
				return (process.env.VUE_APP_CLIENT_AUTH_API || this.clientConfig.apiURLs.auth) + '/ck:' + this.clientKey;
			},
			apiCoreURL: function()
			{
				return process.env.VUE_APP_ROOT_API + '/ck:' + this.clientKey;
			},
			loginURL: function()
			{
				return process.env.VUE_APP_CLIENT_LOGIN_URL || this.clientConfig.loginURL;
			},
			coreAPI: function()
			{
				const instance = this.$http.create(
					{
						baseURL: this.apiCoreURL,
						timeout: 1000 * 30,
						withCredentials: true
					}
				);
				
				return instance;
			},
			authAPI: function()
			{
				const instance = this.$http.create(
					{
						baseURL: this.apiAuthURL,
						timeout: 1000 * 30,
						withCredentials: true,
						headers: { common: { 'eluminSession': this.authSession } }
					}
				);
				
				return instance;
			},
			portalAPI: function()
			{
				const instance = this.$http.create(
					{
						baseURL: this.apiURL,
						timeout: 1000 * 30,
						withCredentials: true,
						headers: { common: { 'eluminSession': this.userSession } }
					}
				);
				
				instance.interceptors.request.use(
					async function(c)
					{
						const randomTime = Math.floor(Math.random() * (5000 - 2000 + 1) + 2000);
						
						console.log('Sleep for ' + randomTime);
						
						await sleep(randomTime);
						
						return c;
					},
					null,
					{
						runWhen: function(c)
						{
							if(/\/link$/.test(c.url))
								return true;
							
							return false;
						}
					}
				);
				
				return instance;
			},
			build: function()
			{
				return { version: _ELUMIN_BUILD_VERSION, date: _ELUMIN_BUILD_DATE };
			},
			logo: function()
			{
				let logo = undefined;
				
				try
				{
					logo = 'https://s3.amazonaws.com/co.elumin.sites.prd/config/' + this.clientKey[0] + '/' + this.clientKey[1] + '/' + this.clientKey + '/logo.png'
				}
				catch(e)
				{
					logo = require('@/assets/elumin.svg');
				}
				
				return logo;
			},
			whiteLabelTitle: function()
			{
				return (('whiteLabel' in this.clientConfig) && this.clientConfig.whiteLabel.title);
			},
			shouldShowPoweredBy: function()
			{
				if(('whiteLabel' in this.clientConfig) && ('poweredBy' in this.clientConfig.whiteLabel))
					return !this.clientConfig.whiteLabel.poweredBy;
				
				return true;
			},
			shouldShowCopyrightFooter: function()
			{
				if(('whiteLabel' in this.clientConfig) && ('copyrightFooter' in this.clientConfig.whiteLabel))
					return !this.clientConfig.whiteLabel.copyrightFooter;
				
				return true;
			},
			isMaintenanceMode: function()
			{
				if(('maintenance' in this.clientConfig))
					return this.clientConfig.maintenance.enabled
				
				return false;
			},
			maintenanceMessages: function()
			{
				if(!this.isMaintenanceMode || (!('messages' in this.clientConfig.maintenance)))
					return [];
				
				let messages = this.clientConfig.maintenance.messages;
				
				messages.sort((a,b) => ((a.date >= b.date) ? 1 : -1));
				
				return messages.reverse();
			}
		},
		methods:
		{
			init: async function init()
			{
				this.$logger.info('Initializing ' + this.appName.replace(/^./, (c) => c.toUpperCase()) + '...');
				
				if(/^init/i.test(this.$route.name))
				{
					await this.$store.dispatch('clear');
					this.userToken = null;
					
					let params = this.$route.params;
					
					if(('clientKey' in params))
					{
						if(params.clientKey)
						{
							this.$logger.debug('Found clientKey: ' + params.clientKey);
							
							await this.$store.dispatch('clientKey', params.clientKey);
						}
					}
				}
				
				if(!this.clientKey)
					return;
				
// 				if(!('clientCode' in this.clientConfig))
// 				{
// 					const clientConfig = await this.coreAPI.get('/client')
// 						.then(
// 							resp => resp.data
// 						).catch(
// 							error =>
// 							{
// 								if(error.response)
// 								{
// 									return error.response.data;
// 								}
// 								else if(error.request)
// 								{
// 									this.$logger.error(error.request);
// 							
// 									return { code: 'NoResponse', message: 'No response received' };
// 								}
// 								else
// 								{
// 									this.$logger.error('Error', error.message);
// 							
// 									return { code: 'Error', message: error.message };
// 								}
// 							}
// 						);
// 				
// 					if(!clientConfig || clientConfig.status != 'OK')
// 					{
// 						this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Code: ' + clientConfig.code + '<br/>Message: ' + clientConfig.message);
// 				
// 						return;
// 					}
// 				
// 					await this.$store.dispatch('clientConfig', clientConfig.config);
// 				}
				
				await this.getClientConfig();
				
// 				this.$logger.debug(this.clientConfig);
				
				if(('maintenance' in this.clientConfig))
					return;
				
				if(this.$route.params.userToken)
				{
					this.$logger.debug('Found userToken: ' + this.$route.params.userToken);
					
					this.userToken = this.$route.params.userToken;
				}
				
				if(!this.userToken)
				{
					if(await this.getUserToken())
						this.$logger.debug('userToken: ' + this.userToken);
				}
				
				if(this.userToken)
				{
					await this.startSession();
					await this.getProfile();
				}
				
// 				if((this.isSessionValid || (this.profile && 'uuid' in this.profile && this.profile.uuid)) && /^init/i.test(this.$route.name))
				if(/^init/i.test(this.$route.name) && this.clientKey)
				{
					if('rs' in this.$route.query)
						this.$router.push({ path: this.$route.query.rs });
					else
						this.$router.push({ path: '/' });
				}
				
				return;
			},
			loginRedirect: function loginRedirect()
			{
				if(!this.loginURL)
					return this.showError(error.response.statusText);
				
				let redirectURL = this.loginURL + 
					encodeURIComponent(
						(new URL(this.apiAuthURL, window.location.origin)).pathname + '/session/create/' + this.appName + '?rs=' + 
						encodeURIComponent(
							window.location.origin + 
							window.location.pathname +
							this.$router.resolve(
								{
									name: 'Init-ClientKey-UserToken',
									params: { clientKey: this.clientKey, userToken: '|token|' },
									query: ((/^Init-ClientKey/.test(this.$route.name)) ? this.$route.query : { rs: this.$route.fullPath })
								}
							).href
						)
					)
		
				this.$logger.debug('Redirecting to: ' + redirectURL);
				
				window.location.href = redirectURL;
		
				return;
			},
			getUserToken: async function getUserToken()
			{
				let token = await this.authAPI.get('/session/create/' + this.appName)
					.then(
						resp => { return resp.headers.eluminauthtoken; }
					).then(
						token =>
						{
							if(!token)
							{
								this.showError('Unable to retrieve user authentication token');
						
								return;
							}
					
							return token;
						}
					).catch(
						error =>
						{
							if(error.response)
							{
								if(error.response.status === 403)
								{
									this.showError(error.response.data.code);
							
									return;
								}
								
								return this.loginRedirect();
							}
							else if(error.request)
							{
								this.$logger.error(error.request);
						
								this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>');
						
								return;
							}
							else
							{
								this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Message: ' + error.message);
						
								return;
							}
						}
					);
				
				return this.userToken = token;
			},
			startSession: async function startSession()
			{
				let r = await this.portalAPI.post('/session/start', { token: this.userToken })
					.then(
						async resp =>
						{
							if('eluminauthsession' in resp.headers)
							{
								this.$logger.debug('Found authSession: ', resp.headers.eluminauthsession);
								
								await this.$store.dispatch('authSession', resp.headers.eluminauthsession);
								
								this.$logger.debug('Stored authSession: ', this.authSession);
							}
							
							if('eluminsession' in resp.headers)
							{
								this.$logger.debug('Found userSession: ', resp.headers.eluminsession);
								
								await this.$store.dispatch('userSession', resp.headers.eluminsession);
								
								this.$logger.debug('Stored userSession: ', this.userSession);
							}
							
							return;
						}
					).catch(
						async error =>
						{
							this.$logger.debug(error);
							
							if(error.response)
							{
								await this.$store.dispatch('authSession', "");
								await this.$store.dispatch('userSession', "");
								
								return;
							}
							else if(error.request)
							{
								this.$logger.error(error.request);
								
								return { code: 'NoResponse', message: 'No response received' };
							}
							else
							{
								this.$logger.error('Error', error.message);
								
								return { code: 'Error', message: error.message };
							}
						}
					);
				
				if(r && 'message' in r)
				{
					this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Message: ' + r.message);
				}
				
				return r;
			},
			getProfile: async function getProfile()
			{
				let r = await this.portalAPI.get('/session')
					.then(
						async resp => await this.$store.dispatch('profile', resp.data)
					).catch(
						async error =>
						{
							if(error.response)
							{
								return await this.$store.dispatch('profile', null);
							}
							else if(error.request)
							{
								this.$logger.error(error.request);
								
								return { code: 'NoResponse', message: 'No response received' };
							}
							else
							{
								this.$logger.error('Error', error.message);
								
								return { code: 'Error', message: error.message };
							}
						}
					);
				
				if(r && 'message' in r)
				{
					this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Message: ' + r.message);
				}
				
				return r;
			},
			getClientConfig: async function getClientConfig()
			{
				const clientConfig = await this.coreAPI.get('/client')
					.then(
						resp => { return Promise.resolve(resp.data); }
					).catch(
						error =>
						{
							if(error.response)
							{
								return Promise.reject(error.response.data);
							}
							else if(error.request)
							{
								this.$logger.error(error.request);
						
								return Promise.reject({ code: 'NoResponse', message: 'No response received' });
							}
							else
							{
								this.$logger.error('Error', error.message);
						
								return Promise.reject({ code: 'Error', message: error.message });
							}
						}
					);
				
				if(!clientConfig || clientConfig.status != 'OK')
				{
					this.showError('<p>There was an error setting up the application. Please contact support if this error persists.</p>Code: ' + clientConfig.code + '<br/>Message: ' + clientConfig.message);
					
					return;
				}
				
				await this.$store.dispatch('clientConfig', clientConfig.config);
			},
			showError: function(message)
			{
				let data = this.errorModalData;
				
				this.$logger.error(message);
				data.message = message;
				
				this.$root.$emit('bv::show::modal', data.id);
				
				return false;
			},
			slugify: function(text)
			{
				return text.toString().toLowerCase()
					.replace(/\s+/g, '-')  
					.replace(/[^\w\-]+/g, '') 
					.replace(/\-\-+/g, '-')  
					.replace(/^-+/, '')  
					.replace(/-+$/, ''); 
			}
		},
		created()
		{
			let vm = this;
			
			if(/^demo/i.test(this.$route.name))
				return;
			
			this.$nextTick(this.init);
		},
		mounted()
		{
			this.$store.commit('loadedAt', new Date());
	
			this.isReady = true;
		},
		beforeDestroy()
		{
			listeners.forEach(
				(v) =>
				{
					this.$logger.debug('Deregistering listener: ' + v);
					
					this.$root.$off(v);
				}
			);
		},
		watch:
		{
			'$route.params': function(params)
			{
				if(/^init/i.test(this.$route.name))
					this.$nextTick(this.init);
			},
			
			clientConfig: async function(nVal, oVal)
			{
				if(('maintenance' in nVal) && nVal.maintenance.enabled)
				{
					if(!this.timers.refreshClientConfig.isRunning)
						this.$timer.start('refreshClientConfig');
				}
				
				if((!('maintenance' in nVal) || (!nVal.maintenance.enabled)) && (('maintenance' in oVal) && (oVal.maintenance.enabled)))
				{
					if(this.timers.refreshClientConfig.isRunning)
						this.$timer.stop('refreshClientConfig');
					
					this.$router.go();
				}
				
				return true;
// 			},
// 			
// 			clientKey: async function(nVal, oVal)
// 			{
// 				if(!!nVal)
// 					this.$nextTick(this.getClientConfig);
// 				
// 				return true;
			}
		},
		timers:
		{
			refreshApp:
			{
				callback: function()
				{
					if((new Date() - this.loadedAt) > (1000 * 60 * 60))
						this.$router.go();
				},
				autostart: true,
				repeat: true,
				immediate: false,
				time: 1000 * 60 * 5
			},
			refreshClientConfig:
			{
				callback: function() { this.getClientConfig(); },
				autostart: false,
				repeat: true,
				immediate: true,
				time: 1000 * 60
			}
		},
		data()
		{
			return {
				isReady: false,
				userToken: null,
				errorModalData:
				{
					id: 'errorModal',
					message: undefined,
					allowClose: false
				}
			}
		}
	}
</script>

<style>
	#app
	{
		font-family: "Roboto", sans-serif;
		height: 100%;
		min-height: 100%;
		max-height: 100%;
	}
	
	#header
	{
		min-height: 64px;
		max-height: 64px;
		
		min-width: 100%;
	}
	
	#header header
	{
		padding: 0 20px;
		min-height: 64px;
	}
	
	#body
	{
		min-height: calc(100% - 96px);
		max-height: calc(100% - 96px);
		
		min-width: 100%;
		
		overflow: auto;
		
		display: flex;
	}
	
	#main-navigation
	{
		min-height: 100%;
		max-height: 100%;

		max-width: 256px;
		min-width: 256px;
		
		overflow: auto;
		
		padding-top: 12px;
		
		display: flex; 
		flex-wrap: wrap;
	}
	
	#main-content
	{
		max-height: 100%;
		
		width:100%;
		max-width: calc(100% - 256px);
		
		overflow: auto;
		
		padding: 12px;
	}
		
	#alert-anchor
	{
		max-width:400px;
		position: fixed;
		z-index: 99999;
		top: 100px;
		right: 30px;
	}
	
	#footer
	{
		position: relative;
		
		min-height: 32px;
		max-height: 32px;
		
		min-width: 100%;
		
		overflow: visible;
		
		line-height: 30px;
		
		font-size: 12px;
	}

	#powered-by-elumin {
		width:100%; 
		margin-top:auto;
		padding-left:20px;
	}

	#powered-by-notice
	{
		margin-top: auto;
		margin-left: auto;
		margin-right: auto;
		
		line-height: initial;
	}
	
	.navbar-text
	{
		color: var(--dark) !important;
	}
	
	.btn-dark:hover:not(:disabled), .btn-dark-active
	{
		background-color: var(--blue) !important;
		border-color: var(--blue) !important;
	}
	
	.fade-enter-active, .fade-leave-active
	{
		transition: all 0.25s;
	}
	
	.fade-enter, .fade-leave-to
	{
		opacity: 0;
	}
	
	.powered-by-text {
		margin-left:28px;
		font-size:14px;
		position: relative;
		bottom: 3px
	}
</style>
